import { useEffect, useState, useRef } from 'react';
import { Flex, StyledImg } from '../../../component-library';
import { VideoChat } from '../../../components';
import { useReceiveMessage, usePostMessage } from '../../../context/messageContext';
import astigmatismClockImage from '../../../assets/img/astigmatism-clock.jpg';
import { useCalibration } from '../../../context/calibrationContext';
import { useToolDetection } from '../../../helpers/distance-detection-hooks';
import { calibrationConstants } from '../../../helpers/calibration-constants';
import { colorFilters } from '../../../helpers/refraction';
import { kaysOptotypes } from '../../../helpers/kaysOptotypes';

const ResponsiveRefractionTestPatient = () => {
  const {
    calibrationData: { perceivedFocalLength },
  } = useCalibration();
  const messageFromOptician = useReceiveMessage();
  const postMessage = usePostMessage();

  const [stimulusColor, setStimulusColor] = useState(null);
  const [stimulusBackground, setStimulusBackground] = useState(null);
  const [stimulusSize, setStimulusSize] = useState(null);
  const [stimulusType, setStimulusType] = useState(null);
  const [selectedOptotype, setSelectedOptotype] = useState(null);
  const [visibleStimulus, setVisibleStimulus] = useState(false);
  const [pixelDensity, setPixelDensity] = useState(0);

  const [showAstigmatismClock, setShowAstigmatismClock] = useState(false);
  const canvasDOM = useRef(null);
  const videoDOM = useRef(null);
  const { setShouldCollectToolDistance, toolDistance } = useToolDetection({
    videoDOM,
    canvasDOM,
    perceivedFocalLength: perceivedFocalLength ? perceivedFocalLength : calibrationConstants.perceivedFocalLength,
  });

  const videoChatCustomStyle = {
    videoBox: {
      position: 'fixed',
      top: 0,
      left: 0,
      visibility: '',
      width: 100,
      height: 100,
      overflow: 'hidden',
    },
    localParticipant: {
      overflow: 'hidden',
    },
  };

  const updateTarget = (newTarget) => {
    setStimulusColor(newTarget.stimulusColor);
    setStimulusType(newTarget.stimulusType);
    setSelectedOptotype(newTarget.stimulusOptotype);
    setStimulusSize(newTarget.stimulusSize);
    setVisibleStimulus(newTarget.visibleStimulus);
    setStimulusBackground(newTarget.stimulusBackground);
    setShowAstigmatismClock(newTarget.showAstigmatismClock);
    setPixelDensity(newTarget.pixelDensity);
  };

  const getTarget = () => {
    const stimulusSizeInPx = Math.round(stimulusSize / pixelDensity);
    const targetStyle = {
      width: Math.ceil(stimulusSizeInPx),
      height: Math.ceil(stimulusSizeInPx),

      position: 'absolute',
      borderRadius: Math.round(stimulusSizeInPx / 2),
      top: 25 / pixelDensity,
      left: window.screen.width / 2 - Math.round(stimulusSizeInPx / 2),
      display: visibleStimulus === true ? 'block' : 'none',
    };

    return (
      <Flex style={{ ...targetStyle, backgroundColor: stimulusType === 'dot' ? '#000' : 'transparent', filter: colorFilters[stimulusColor] }}>
        {stimulusType === 'optotype' && kaysOptotypes[selectedOptotype]}
      </Flex>
    );
  };

  const getTargetBackground = () => {
    if (showAstigmatismClock === true) {
      return <StyledImg src={astigmatismClockImage} alt='Astigmatism Clock' />;
    }

    return stimulusBackground && stimulusBackground.media && stimulusBackground.label ? (
      <StyledImg src={stimulusBackground.media} alt={stimulusBackground.label} />
    ) : null;
  };

  useEffect(() => {
    switch (messageFromOptician.type) {
      case 'updateTarget':
        updateTarget(messageFromOptician.content);
        break;
      case 'detectDistance':
        setShouldCollectToolDistance(true);
        break;
      case 'stopDetections':
        setShouldCollectToolDistance(false);
        break;
      default:
        break;
    }
  }, [messageFromOptician, setShouldCollectToolDistance]);

  useEffect(() => {
    postMessage({ type: 'patientDistance', content: toolDistance });
  }, [postMessage, toolDistance]);

  return (
    <Flex>
      <VideoChat stacked customStyle={videoChatCustomStyle} parentVideoRef={videoDOM} />
      <Flex width='100vw' height='100vh' overflow='hidden' justifyContent='center' bg={stimulusBackground?.value || '#fff'} style={{ zIndex: 9 }}>
        <canvas id='canvas' style={{ display: 'none' }} ref={canvasDOM}></canvas>
        {getTargetBackground()}
        {getTarget()}
      </Flex>
    </Flex>
  );
};

export default ResponsiveRefractionTestPatient;
